import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useTeamMembers } from '../../../hooks/useTeamMembers';
import { TeamMember } from '../types/teamMember';

interface SelectedMemberContextType {
  selectedMember: TeamMember | null;
  setSelectedMember: (member: TeamMember | null) => void;
}

const SelectedMemberContext = createContext<
  SelectedMemberContextType | undefined
>(undefined);

export function SelectedMemberProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: teamMembers, isLoading } = useTeamMembers();

  useEffect(() => {
    if (!isLoading && teamMembers) {
      const memberId = searchParams.get('memberId');
      const member = teamMembers.find((m) => m.id === memberId);

      if (member) {
        setSelectedMember(member);
      } else if (teamMembers[0]) {
        // Select first member by default
        setSelectedMember(teamMembers[0]);
        setSearchParams(
          {
            memberId: teamMembers[0].id,
            employeeId: teamMembers[0].employeeId,
          },
          { replace: true },
        );
      }
    }
  }, [teamMembers, isLoading, searchParams, setSearchParams]);

  const handleSetSelectedMember = React.useCallback(
    (member: TeamMember | null): void => {
      setSelectedMember(member);
      if (member) {
        setSearchParams(
          { memberId: member.id, employeeId: member.employeeId },
          { replace: true },
        );
      }
    },
    [setSearchParams],
  );

  const contextValue = React.useMemo(
    () => ({
      selectedMember,
      setSelectedMember: handleSetSelectedMember,
    }),
    [selectedMember, handleSetSelectedMember],
  );

  return (
    <SelectedMemberContext.Provider value={contextValue}>
      {children}
    </SelectedMemberContext.Provider>
  );
}

export function useSelectedMember(): SelectedMemberContextType {
  const context = useContext(SelectedMemberContext);
  if (context === undefined) {
    throw new Error(
      'useSelectedMember must be used within a SelectedMemberProvider',
    );
  }
  return context;
}
